"use client";

import { c as _c } from "react/compiler-runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import React, { forwardRef, useImperativeHandle } from "react";
import { Controller, useForm } from "react-hook-form";
import { FiX } from "react-icons/fi";
import { Button, Input, Textarea } from "@/components/ui";
import { DatePicker } from "@/components/ui/date-picker";
import PhoneNumberInput from "@/components/ui/phone-number-input";
import { cx } from "@/lib/tailwind-utils";
import { GiftCardRecipientInput } from "@/types/shop";
const recipientSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phone: z.string(),
  note: z.string(),
  scheduledSendAt: z.date().optional()
}).superRefine((data, ctx) => {
  const trimmedEmail = data.email.trim();
  const hasEmail = trimmedEmail.length > 0;
  // PhoneNumberInput stores digits-only; "10 digits" is the same canonical
  // form `PatientCreate.clean_phone_number` accepts on the backend.
  const phoneDigits = data.phone.replace(/\D/g, "");
  const hasPhone = phoneDigits.length > 0;
  if (!hasEmail && !hasPhone) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["email"],
      message: "Add an email or phone number."
    });
  }
  if (hasEmail && !z.email().safeParse(trimmedEmail).success) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["email"],
      message: "Enter a valid email address."
    });
  }
  if (hasPhone && phoneDigits.length !== 10) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["phone"],
      message: "Enter a 10-digit phone number."
    });
  }
  if (data.scheduledSendAt && data.scheduledSendAt.getTime() <= Date.now()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["scheduledSendAt"],
      message: "Pick a future date and time."
    });
  }
});
type RecipientFormValues = z.infer<typeof recipientSchema>;
const trimOrNull = (s: string): string | null => {
  const v = s.trim();
  return v.length > 0 ? v : null;
};
const toFormValues = (r?: GiftCardRecipientInput): RecipientFormValues => ({
  firstName: r?.firstName ?? "",
  lastName: r?.lastName ?? "",
  email: r?.email ?? "",
  phone: r?.phone ?? "",
  note: r?.note ?? "",
  scheduledSendAt: r?.scheduledSendAt ? new Date(r.scheduledSendAt) : undefined
});
const toRecipient = (values: RecipientFormValues): GiftCardRecipientInput => ({
  firstName: trimOrNull(values.firstName),
  lastName: trimOrNull(values.lastName),
  email: trimOrNull(values.email),
  phone: trimOrNull(values.phone),
  note: trimOrNull(values.note),
  scheduledSendAt: values.scheduledSendAt ? values.scheduledSendAt.toISOString() : null
});

// Midnight today, in local time. The calendar disables anything strictly
// before this — selecting today is still allowed (the time picker is
// validated separately so users can pick a later time today).
const startOfToday = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};
const FieldLabel: React.FC<{
  htmlFor?: string;
  required?: boolean;
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(6);
  const {
    htmlFor,
    required,
    children
  } = t0;
  let t1;
  if ($[0] !== required) {
    t1 = required && <span className="ml-0.5 text-status-error-text">*</span>;
    $[0] = required;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== htmlFor || $[4] !== t1) {
    t2 = <label htmlFor={htmlFor} className="text-body-strong text-foreground">{children}{t1}</label>;
    $[2] = children;
    $[3] = htmlFor;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export interface RecipientFormHandle {
  /** Trigger form submit. Resolves once validation + onSave has completed. */
  submit: () => Promise<void>;
}
interface RecipientFormProps {
  initial?: GiftCardRecipientInput;
  /**
   * When set, renders as the form's own header. Omit when a parent already
   * shows the heading (e.g. the per-card stepper renders "Card 2 of 3").
   */
  title?: string;
  /**
   * When true, hides the internal Save button — the parent owns the submit
   * action and triggers it imperatively via the ref. Used by the per-card
   * configurator stepper so a single "Next" button drives the flow.
   */
  hideSubmitButton?: boolean;
  /** Submit button label when shown. */
  submitLabel?: string;
  onSave: (recipient: GiftCardRecipientInput) => void;
  onCancel?: () => void;
}

/**
 * Single recipient capture form: name, email, phone, optional note,
 * optional scheduled send date. Uses react-hook-form + zod so inputs
 * are uncontrolled (avoids the focus/typing glitches that come with
 * fully-controlled forms in a Radix Dialog) and validation rules live
 * next to the schema.
 *
 * Exposes a `submit()` handle so a parent stepper can advance from a
 * single "Next" button instead of two competing CTAs.
 */
const RecipientForm = forwardRef<RecipientFormHandle, RecipientFormProps>(function RecipientForm(t0, ref) {
  const $ = _c(66);
  const {
    initial,
    title,
    hideSubmitButton,
    submitLabel,
    onSave,
    onCancel
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = zodResolver(recipientSchema);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== initial) {
    t2 = toFormValues(initial);
    $[1] = initial;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = {
      resolver: t1,
      defaultValues: t2,
      mode: "onSubmit"
    };
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    register,
    control,
    handleSubmit,
    formState: t4
  } = useForm(t3);
  const {
    errors
  } = t4;
  let t5;
  if ($[5] !== onSave) {
    t5 = values => onSave(toRecipient(values));
    $[5] = onSave;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== handleSubmit || $[8] !== t5) {
    t6 = handleSubmit(t5);
    $[7] = handleSubmit;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const submit = t6;
  let t7;
  if ($[10] !== submit) {
    t7 = () => ({
      submit: async () => {
        await submit();
      }
    });
    $[10] = submit;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  useImperativeHandle(ref, t7);
  let t8;
  if ($[12] !== onCancel || $[13] !== title) {
    t8 = (title || onCancel) && <div className="flex items-center justify-between">{title && <h4 className="text-body-emphasis text-foreground">{title}</h4>}{onCancel && <button type="button" onClick={onCancel} className="cursor-pointer text-caption text-foreground-secondary hover:text-foreground">Cancel</button>}</div>;
    $[12] = onCancel;
    $[13] = title;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <FieldLabel htmlFor="recipient-first-name">First name</FieldLabel>;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== register) {
    t10 = register("firstName");
    $[16] = register;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10) {
    t11 = <div className="space-y-1.5">{t9}<Input id="recipient-first-name" placeholder="Jane" autoComplete="given-name" {...t10} /></div>;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <FieldLabel htmlFor="recipient-last-name">Last name</FieldLabel>;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== register) {
    t13 = register("lastName");
    $[21] = register;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t13) {
    t14 = <div className="space-y-1.5">{t12}<Input id="recipient-last-name" placeholder="Doe" autoComplete="family-name" {...t13} /></div>;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t11 || $[26] !== t14) {
    t15 = <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{t11}{t14}</div>;
    $[25] = t11;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <FieldLabel htmlFor="recipient-email" required={true}>Email</FieldLabel>;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  const t17 = !!errors.email;
  const t18 = errors.email?.message;
  let t19;
  if ($[29] !== register) {
    t19 = register("email");
    $[29] = register;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== t17 || $[32] !== t18 || $[33] !== t19) {
    t20 = <div className="space-y-1.5">{t16}<Input id="recipient-email" type="email" placeholder="recipient@example.com" autoComplete="email" hasError={t17} errorMessage={t18} {...t19} /></div>;
    $[31] = t17;
    $[32] = t18;
    $[33] = t19;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <FieldLabel htmlFor="recipient-phone" required={true}>Phone</FieldLabel>;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== control) {
    t22 = <Controller control={control} name="phone" render={_temp} />;
    $[36] = control;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <p className="text-caption text-foreground-secondary">Add an email or phone number.</p>;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  let t24;
  if ($[39] !== t22) {
    t24 = <div className="space-y-1.5">{t21}{t22}{t23}</div>;
    $[39] = t22;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  let t25;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <FieldLabel htmlFor="recipient-note">Note from sender</FieldLabel>;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  let t26;
  if ($[42] !== register) {
    t26 = register("note");
    $[42] = register;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  let t27;
  if ($[44] !== t26) {
    t27 = <div className="space-y-1.5">{t25}<Textarea id="recipient-note" rows={3} {...t26} /></div>;
    $[44] = t26;
    $[45] = t27;
  } else {
    t27 = $[45];
  }
  let t28;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <FieldLabel>Send when</FieldLabel>;
    $[46] = t28;
  } else {
    t28 = $[46];
  }
  let t29;
  if ($[47] !== control) {
    t29 = <Controller control={control} name="scheduledSendAt" render={_temp3} />;
    $[47] = control;
    $[48] = t29;
  } else {
    t29 = $[48];
  }
  let t30;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <p className="text-caption text-foreground-secondary">Leave blank to send as soon as the payment clears.</p>;
    $[49] = t30;
  } else {
    t30 = $[49];
  }
  let t31;
  if ($[50] !== t29) {
    t31 = <div className="space-y-1.5">{t28}{t29}{t30}</div>;
    $[50] = t29;
    $[51] = t31;
  } else {
    t31 = $[51];
  }
  let t32;
  if ($[52] !== t15 || $[53] !== t20 || $[54] !== t24 || $[55] !== t27 || $[56] !== t31) {
    t32 = <div className="space-y-3">{t15}{t20}{t24}{t27}{t31}</div>;
    $[52] = t15;
    $[53] = t20;
    $[54] = t24;
    $[55] = t27;
    $[56] = t31;
    $[57] = t32;
  } else {
    t32 = $[57];
  }
  let t33;
  if ($[58] !== hideSubmitButton || $[59] !== submitLabel) {
    t33 = !hideSubmitButton && <div className="flex items-center justify-end pt-1"><Button type="submit" variant="primary" className="cursor-pointer"><span>{submitLabel ?? "Save recipient"}</span></Button></div>;
    $[58] = hideSubmitButton;
    $[59] = submitLabel;
    $[60] = t33;
  } else {
    t33 = $[60];
  }
  let t34;
  if ($[61] !== submit || $[62] !== t32 || $[63] !== t33 || $[64] !== t8) {
    t34 = <form onSubmit={submit} className="space-y-5" noValidate={true}>{t8}{t32}{t33}</form>;
    $[61] = submit;
    $[62] = t32;
    $[63] = t33;
    $[64] = t8;
    $[65] = t34;
  } else {
    t34 = $[65];
  }
  return t34;
});
export default RecipientForm;
function _temp(t0) {
  const {
    field,
    fieldState
  } = t0;
  return <PhoneNumberInput id="recipient-phone" placeholder="(555) 123-4567" value={field.value} onChange={e => field.onChange(e.target.value)} hasError={!!fieldState.error} errorMessage={fieldState.error?.message} />;
}
function _temp2(e_1) {
  return e_1.stopPropagation();
}
function _temp3(t0) {
  const {
    field: field_0,
    fieldState: fieldState_0
  } = t0;
  return <><div className="relative"><DatePicker value={field_0.value} onChange={field_0.onChange} showTimePicker={true} placeholder="Send immediately" disabledDays={{
        before: startOfToday()
      }} className={cx("text-body", field_0.value && "pr-9")} />{field_0.value && <button type="button" onClick={e_0 => {
        e_0.stopPropagation();
        e_0.preventDefault();
        field_0.onChange(undefined);
      }} onPointerDown={_temp2} aria-label="Clear scheduled send date" className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded text-foreground-secondary transition-colors hover:bg-hover hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"><FiX className="h-3.5 w-3.5" /></button>}</div>{fieldState_0.error?.message && <p className="text-caption text-status-error-text">{fieldState_0.error.message}</p>}</>;
}